/**
 * Copilot action executor — turns declarative CopilotActions (Action
 * Protocol) into concrete SPA effects:
 *
 *  - navigate           -> react-router navigation (helper injected by
 *                          CopilotProvider)
 *  - open_form          -> page-effects consumer (handler or marked opener)
 *  - fill_field/fill_form -> page-effects consumer (handler or registered
 *                          form fields, with React-compatible input events)
 *  - submit_form        -> page-effects consumer (handler or resolvable <form>)
 *  - select_table_rows  -> page-effects consumer (handler or per-row checkboxes)
 *  - highlight          -> page-effects consumer (scroll-to + outline pulse)
 *  - open_modal         -> page-effects consumer (handler or marked opener)
 *  - scroll_to          -> page-effects consumer (selector / field / form / page)
 *  - toast              -> toast pushed into the copilot store
 *  - import_records     -> entity-resolved create POSTs (or payload.endpoint)
 *  - wait_user_confirm  -> 'skipped' via this executor (handled upstream by the
 *                          confirmation UI). The copilot store's opt-in PACED
 *                          player (copilot-store.ts `runPlayerLoop`) intercepts
 *                          this verb earlier via `runPacedStep`/`isWaitUserConfirm`
 *                          below and PAUSES the run instead — this executor's own
 *                          'skipped' no-op is unchanged for the direct confirm-card
 *                          / auto-run paths (additive: default behaviour intact).
 *
 * The fill/submit/highlight/open_form effects are actually PERFORMED by the
 * generic `page-effects` consumer against the context registered via
 * `useCopilotPage` — so a confirmed action produces a real, visible effect or
 * an honest `skipped` result instead of a false "executed". The legacy
 * `copilot:*` CustomEvents are still dispatched as advisory notifications for
 * any external listener, but the returned status comes from the performer.
 * Every execution is recorded in the copilot store timeline.
 */

import { api } from '@/lib/api/client';
import { useCopilotStore } from '@/stores/copilot-store';

import {
  performFill,
  performHighlight,
  performOpenForm,
  performOpenModal,
  performScrollTo,
  performSelectRows,
  performSubmit,
} from './page-effects';
import type { CopilotAction } from './types';

export interface ExecutorHelpers {
  navigate: (to: string) => void;
}

export interface ExecutionResult {
  status: 'executed' | 'failed' | 'skipped';
  detail?: string;
}

let registeredHelpers: ExecutorHelpers = {
  navigate: () => {
    // CopilotProvider not mounted yet — nothing to navigate with.
    console.warn('[copilot] navigation demandée avant le montage de CopilotProvider');
  },
};

/** Called by CopilotProvider to inject the react-router navigate function. */
export function setExecutorHelpers(helpers: ExecutorHelpers): void {
  registeredHelpers = helpers;
}

/** Executes an action with the helpers registered by CopilotProvider. */
export function runRegisteredAction(action: CopilotAction): Promise<ExecutionResult> {
  return executeAction(action, registeredHelpers);
}

// ---------------------------------------------------------------------------
// Paced player support — the copilot store's opt-in step-by-step "watch &
// intervene" mode runs the action queue ONE STEP AT A TIME through
// `runPacedStep` instead of firing every non-confirm action back-to-back
// (the default `sendMessage` auto-run loop, untouched, still does that).
// `wait_user_confirm` is intercepted HERE — before it ever reaches
// `perform()` above — so the direct confirm-card / auto-run path keeps
// returning the original `'skipped'` no-op, while the paced loop gets an
// honoured pause point instead.
// ---------------------------------------------------------------------------

/** The paced player's inter-step pace, chosen by the user (lent/normal/rapide). */
export type CopilotPace = 'lent' | 'normal' | 'rapide';

/** Inter-step delay for the paced player, in ms (added on top of the existing post-navigate mount wait). */
export function paceDelayMs(pace: CopilotPace): number {
  switch (pace) {
    case 'lent':
      return 1600;
    case 'rapide':
      return 250;
    case 'normal':
    default:
      return 800;
  }
}

export function isWaitUserConfirm(action: CopilotAction): boolean {
  return action.type === 'wait_user_confirm';
}

/** Human-readable prompt carried by a `wait_user_confirm` action's payload. */
function extractWaitConfirmPrompt(action: CopilotAction): string {
  const payload = action.payload;
  if (typeof payload === 'string' && payload.trim()) return payload;
  if (payload !== null && typeof payload === 'object' && 'prompt' in payload) {
    const prompt = (payload as { prompt?: unknown }).prompt;
    if (typeof prompt === 'string' && prompt.trim()) return prompt;
  }
  return 'Le copilot attend votre confirmation pour continuer.';
}

export type PacedStepOutcome =
  | { kind: 'waiting'; prompt: string }
  | { kind: 'result'; result: ExecutionResult };

/**
 * Runs one action for the paced player. `wait_user_confirm` PAUSES the run —
 * it returns `{kind:'waiting', prompt}` instead of executing anything, and the
 * caller (copilot-store.ts `runPlayerLoop`) is responsible for resuming past
 * it once the user answers. Every other verb runs through the normal
 * `runRegisteredAction`, so it uses the exact same helpers/DOM effects and is
 * recorded in the exact same global timeline as an auto-run or confirmed action.
 */
export async function runPacedStep(action: CopilotAction): Promise<PacedStepOutcome> {
  if (isWaitUserConfirm(action)) {
    return { kind: 'waiting', prompt: extractWaitConfirmPrompt(action) };
  }
  const result = await runRegisteredAction(action);
  return { kind: 'result', result };
}

function dispatchCopilotEvent(name: string, detail: Record<string, unknown>): void {
  window.dispatchEvent(new CustomEvent(name, { detail }));
}

// ---------------------------------------------------------------------------
// import_records — resolve a concrete API target from the proposed action.
//
// The copilot backend proposes `import_records({entity}, {records})` but does
// NOT carry a concrete endpoint (mirroring navigate, where the backend owns
// page_id→route while the executor owns the SPA effect). Earlier this made a
// confirmed CSV import die with "payload.endpoint manquant" — the proposal
// rendered but nothing imported. The executor owns the SPA's API surface, so it
// resolves the endpoint (and per-record shape) from `target.entity` here.
//
// The platform exposes single-object create endpoints (no array/bulk create),
// so each row is created individually. tenant_id/author_id are bound server-side
// from the JWT (content-service overwrites body-supplied values), so a body-only
// projection is safe and cannot cross tenants.
// ---------------------------------------------------------------------------

type Row = Record<string, unknown>;

interface ImportTarget {
  endpoint: string;
  /** Whether the endpoint accepts the whole array (rare) or one row per POST. */
  mode: 'array' | 'per-record';
  /** Map an arbitrary source row onto the endpoint's create body. */
  project?: (row: Row) => Record<string, unknown>;
}

/** First non-empty string among the given case-insensitive row keys. */
function pick(row: Row, keys: string[]): string | undefined {
  const lower: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(row)) lower[k.toLowerCase().trim()] = v;
  for (const key of keys) {
    const v = lower[key];
    if (typeof v === 'string' && v.trim()) return v.trim();
    if (typeof v === 'number') return String(v);
  }
  return undefined;
}

function projectContent(row: Row): Record<string, unknown> {
  const body =
    pick(row, ['body', 'content', 'text', 'message', 'description', 'post', 'corps', 'texte']) ??
    Object.values(row)
      .filter((v): v is string => typeof v === 'string' && v.trim().length > 0)
      .join(' — ') ??
    '';
  const title = pick(row, ['title', 'name', 'subject', 'headline', 'titre', 'sujet', 'nom']);
  const platformRaw = pick(row, ['platforms', 'platform', 'network', 'channel', 'plateforme', 'réseau', 'reseau']);
  const platforms = platformRaw
    ? platformRaw
        .split(/[,;/|]+/)
        .map((s) => s.trim().toLowerCase())
        .filter(Boolean)
    : undefined;
  // tenant_id/author_id are re-bound from the JWT server-side; sent empty to
  // satisfy the schema without ever asserting another tenant/author.
  return {
    body,
    ...(title ? { title } : {}),
    ...(platforms && platforms.length ? { platforms } : {}),
    tenant_id: '',
    author_id: '',
  };
}

// Map a CSV/tabular row onto a CRM contact create body. Handles split
// first/last columns OR a single full-name column, plus email/phone/title.
function projectContact(row: Row): Record<string, unknown> {
  let first = pick(row, ['first_name', 'firstname', 'prénom', 'prenom', 'given_name']);
  let last = pick(row, ['last_name', 'lastname', 'surname', 'family_name', 'nom']);
  if (!first && !last) {
    const full = pick(row, ['name', 'full_name', 'fullname', 'nom complet', 'contact']);
    if (full) {
      const parts = full.split(/\s+/).filter(Boolean);
      first = parts.shift();
      last = parts.length ? parts.join(' ') : undefined;
    }
  }
  const email = pick(row, ['email', 'e-mail', 'courriel', 'mail', 'adresse email']);
  const phone = pick(row, ['phone', 'téléphone', 'telephone', 'tel', 'mobile', 'numéro', 'numero']);
  const jobTitle = pick(row, ['job_title', 'title', 'poste', 'fonction', 'role', 'rôle']);
  return {
    ...(first ? { first_name: first } : {}),
    ...(last ? { last_name: last } : {}),
    ...(email ? { emails: [{ email, primary: true }] } : {}),
    ...(phone ? { phones: [{ phone, primary: true }] } : {}),
    ...(jobTitle ? { job_title: jobTitle } : {}),
    source: 'copilot-import',
  };
}

/** Known entity → import target. Unknown entities fall back to payload.endpoint. */
const ENTITY_IMPORTS: Record<string, ImportTarget> = {
  contents: { endpoint: '/api/v1/contents', mode: 'per-record', project: projectContent },
  content: { endpoint: '/api/v1/contents', mode: 'per-record', project: projectContent },
  contacts: { endpoint: '/api/v1/crm/contacts', mode: 'per-record', project: projectContact },
  contact: { endpoint: '/api/v1/crm/contacts', mode: 'per-record', project: projectContact },
};

function resolveImportTarget(action: CopilotAction): ImportTarget | null {
  const payload = action.payload;
  // An explicit endpoint on the payload always wins (posts the array as-is,
  // the original contract) so a future backend can drive imports directly.
  const explicit: unknown = payload?.endpoint;
  if (typeof explicit === 'string' && explicit.length > 0) {
    return { endpoint: explicit, mode: 'array' };
  }
  const entity = action.target?.entity;
  if (typeof entity === 'string' && ENTITY_IMPORTS[entity.toLowerCase().trim()]) {
    return ENTITY_IMPORTS[entity.toLowerCase().trim()];
  }
  return null;
}

async function importRecords(action: CopilotAction): Promise<ExecutionResult> {
  const target = resolveImportTarget(action);
  if (!target) {
    return {
      status: 'failed',
      detail: `Import impossible : entité inconnue (${action.target?.entity ?? '—'}) et aucun endpoint fourni`,
    };
  }
  const raw: unknown = action.payload?.records ?? [];
  const records: Row[] = Array.isArray(raw) ? (raw as Row[]) : [raw as Row];
  if (records.length === 0) {
    return { status: 'skipped', detail: 'Aucun enregistrement à importer' };
  }

  if (target.mode === 'array') {
    try {
      await api.post(target.endpoint, records);
      return { status: 'executed', detail: `${records.length} enregistrement(s) importé(s) via ${target.endpoint}` };
    } catch (error) {
      return { status: 'failed', detail: error instanceof Error ? error.message : `Import échoué (${target.endpoint})` };
    }
  }

  // Per-record create: post each projected row; report an honest aggregate.
  let ok = 0;
  let firstError: string | undefined;
  for (const row of records) {
    const body = target.project ? target.project(row) : row;
    try {
      await api.post(target.endpoint, body);
      ok += 1;
    } catch (error) {
      if (!firstError) firstError = error instanceof Error ? error.message : String(error);
    }
  }
  if (ok === 0) {
    return { status: 'failed', detail: `Import échoué (${target.endpoint})${firstError ? ` : ${firstError}` : ''}` };
  }
  const failed = records.length - ok;
  return {
    status: 'executed',
    detail:
      `${ok} enregistrement(s) importé(s) via ${target.endpoint}` +
      (failed > 0 ? ` (${failed} en échec)` : ''),
  };
}

// ---------------------------------------------------------------------------
// execute_tool — a backend tool (CRM mutation / publish / run workflow / ...).
//
// Unlike the UI verbs, this is NOT run in the browser: confirming it tells the
// copilot SERVICE to execute the tool server-side (POST /actions/{id}/confirm),
// where the capability sandbox, HIL and fail-closed CASL gate live. The endpoint
// returns the executed action carrying the real outcome. This is the copilot's
// "do anything" execution path — the browser never touches the tool directly, so
// tenant isolation + compliance are enforced on the server.
// ---------------------------------------------------------------------------

async function executeBackendTool(action: CopilotAction): Promise<ExecutionResult> {
  const toolName = action.target?.tool ?? action.target?.label ?? 'outil';
  try {
    const res = await api.post<{ status?: string; result?: { ok?: boolean; reason?: string } }>(
      `/api/v1/copilot/actions/${action.action_id}/confirm`,
    );
    const ok = res?.status === 'executed' && res?.result?.ok !== false;
    if (ok) {
      return { status: 'executed', detail: `Outil exécuté : ${toolName}` };
    }
    const reason = res?.result?.reason;
    return {
      status: 'failed',
      detail: reason ? `${toolName} : ${reason}` : `Échec de l'outil ${toolName}`,
    };
  } catch (error) {
    return {
      status: 'failed',
      detail: error instanceof Error ? error.message : `Échec de l'outil ${toolName}`,
    };
  }
}

/**
 * Executes a single action and records the outcome in the store timeline.
 * Never throws — failures are reported as { status: 'failed' }.
 */
export async function executeAction(
  action: CopilotAction,
  helpers: ExecutorHelpers,
): Promise<ExecutionResult> {
  let result: ExecutionResult;
  try {
    result = await perform(action, helpers);
  } catch (error) {
    result = {
      status: 'failed',
      detail: error instanceof Error ? error.message : String(error),
    };
  }
  useCopilotStore.getState().recordExecution(action, result.status, result.detail);
  return result;
}

async function perform(action: CopilotAction, helpers: ExecutorHelpers): Promise<ExecutionResult> {
  switch (action.type) {
    case 'navigate': {
      const route = action.target?.route;
      if (!route) return { status: 'failed', detail: 'target.route manquant' };
      helpers.navigate(route);
      return { status: 'executed', detail: `Navigation vers ${route}` };
    }

    case 'open_form': {
      dispatchCopilotEvent('copilot:open-form', {
        pageId: action.target?.page_id,
        formId: action.target?.form_id,
        payload: action.payload,
      });
      return performOpenForm(action.target, action.payload);
    }

    case 'fill_field': {
      const field = action.target?.field;
      if (!field) return { status: 'failed', detail: 'target.field manquant' };
      const payload = action.payload;
      const value =
        payload !== null && typeof payload === 'object' && 'value' in payload
          ? payload.value
          : payload;
      const fields = { [field]: value };
      dispatchCopilotEvent('copilot:fill', { formId: action.target?.form_id, fields });
      return performFill(fields, action.target?.form_id);
    }

    case 'fill_form': {
      const payload = action.payload;
      const fields: Record<string, unknown> =
        payload !== null && typeof payload === 'object' && 'fields' in payload
          ? payload.fields
          : (payload ?? {});
      dispatchCopilotEvent('copilot:fill', {
        formId: action.target?.form_id,
        fields,
      });
      return performFill(fields, action.target?.form_id);
    }

    case 'submit_form': {
      // Persist server-side first: a confirmed submit_form on a known CRM create
      // form is bridged by the copilot service to the real crm_create_* tool
      // (reliable, no dependency on resolving the exact DOM <form>). Only when the
      // backend does not execute it (non-CRM form) do we fall back to the
      // client-side submit. Backend-first also avoids a double-create.
      return executeBackendTool(action).then((backend) => {
        if (backend.status === 'executed') return backend;
        dispatchCopilotEvent('copilot:submit', { formId: action.target?.form_id });
        return performSubmit(action.target?.form_id);
      });
    }

    case 'select_table_rows': {
      dispatchCopilotEvent('copilot:select-rows', {
        tableId: action.target?.table_id,
        payload: action.payload,
      });
      return performSelectRows(action.target, action.payload);
    }

    case 'open_modal': {
      dispatchCopilotEvent('copilot:open-modal', {
        modalId: action.target?.modal_id,
        payload: action.payload,
      });
      return performOpenModal(action.target, action.payload);
    }

    case 'scroll_to': {
      dispatchCopilotEvent('copilot:scroll-to', {
        selector: action.target?.selector,
        field: action.target?.field,
        formId: action.target?.form_id,
        pageId: action.target?.page_id,
      });
      return performScrollTo(action.target);
    }

    case 'highlight': {
      dispatchCopilotEvent('copilot:highlight', {
        pageId: action.target?.page_id,
        formId: action.target?.form_id,
        field: action.target?.field,
        payload: action.payload,
      });
      return performHighlight(action.target);
    }

    case 'toast': {
      const payload = action.payload;
      const message =
        typeof payload === 'string'
          ? payload
          : payload !== null && typeof payload === 'object' && 'message' in payload
            ? String(payload.message)
            : 'Notification du copilot';
      useCopilotStore.getState().pushToast(message);
      return { status: 'executed', detail: `Toast : ${message}` };
    }

    case 'import_records':
      return importRecords(action);

    case 'execute_tool':
      return executeBackendTool(action);

    case 'wait_user_confirm':
      return { status: 'skipped', detail: 'Confirmation gérée par l’interface' };

    default:
      return { status: 'failed', detail: 'Type d’action inconnu' };
  }
}
